<template>
  <div class="white-papers">
    <div v-if="content.hero" class="wrapper">
      <h1 class="grid-whitepaper__title" v-html="`${content.title}`"></h1>
      <div
        v-if="content.hero?.subtitle || content.subtitle"
        class="grid-whitepaper__subtitle h3"
        v-html="content.hero?.subtitle || content.subtitle"
      ></div>
    </div>

    <RouterView>
      <Transition
        mode="out-in"
        name="g-fade"
        @enter="onEnter"
        @after-leave="updateResources"
      >
        <GridWhitePaper
          v-if="content.items"
          :key="route.fullPath"
          :content="content?.items"
          :tags="content?.tags"
          :highlight="content.items?.current_page <= 1 && !route.query['tag[]']"
        />
      </Transition>
    </RouterView>

    <div id="archive-results-listing" class="archive__results">
      <Pagination
        v-if="content.items && content.items.pages > 1"
        :key="route.fullPath"
        :base-url="route.path"
        :total="content.items.pages"
      />
    </div>

    <DuoCombo v-if="content.combo" :content="content.combo" />

    <CtaRibbon v-if="content.ctaRibbon" :content="content.ctaRibbon" />
  </div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'

import CtaRibbon from '@/components/cta/Ribbon.vue'
import DuoCombo from '@/components/duo/Combo.vue'
import GridWhitePaper from '@/components/grid/WhitePaper.vue'
import Pagination from '@/components/ui/Pagination.vue'
import { updateResource } from '@/core/resource'
import { prefersReducedMotion } from '@/utils/motion'

import type { PropType } from 'vue'
import type { CardCommon } from '@/components/card/Common.vue'
import type { RibbonCta } from '@/components/cta/Ribbon.vue'
import type { DuoCombo as DuoComboType } from '@/components/duo/Combo.vue'
import type { ResourceSingle } from '@/stores/resource'
import type { Pagination as PaginationType } from '@/types'

const route = useRoute()

export interface WhitePaper {
  title: string
  subtitle?: string
  hero?: {
    title: string
    subtitle: string
  }
  items: PaginationType<CardCommon>
  combo?: DuoComboType
  ctaRibbon?: RibbonCta
  tags?: Record<string, string>
}

defineProps({
  content: {
    type: Object as PropType<WhitePaper>,
    required: true,
    default: () => ({}),
  },
})

const onEnter = (el: Element) => {
  const { top } = el.getBoundingClientRect()

  if (window.scrollY < top) {
    return
  }

  window.scrollTo({
    top,
    left: 0,
    behavior: prefersReducedMotion() ? 'instant' : 'smooth',
  })
}

const updateResources = () => {
  if (route.meta.state) {
    updateResource(route.meta.state.resource as ResourceSingle)
  }
}
</script>

<style lang="scss" scoped>
$bp: 'l';

.white-papers {
  margin-bottom: 6rem;

  @include mq($bp) {
    margin-bottom: 8rem;
    padding-top: 12rem;
  }
}

.grid-whitepaper__title {
  @extend %fw-bold;
  @extend %section-title-spacing;
  @include section-title;
}

.grid-whitepaper__subtitle {
  margin-top: 2rem;
}
</style>
